import { prefersReducedMotion } from '@/shared/lib/viewTransition'

// On return from a deep dive, the card the user came from is reopened and scrolled into view.

type Expanded = { cardId: number; fromKeyboard: boolean }

let cameFrom: string | null = null
const expandedBySection = new Map<string, Expanded | null>()

/** Armed by a deep dive page: the id of the dive the user is looking at. */
export const leaveForHomeFrom = (deepDiveId: string) => {
  cameFrom = deepDiveId
}

/** Consumes the return: the dive's id the first time, null after, for the home page that scrolls to the card. */
export const takeReturn = (): string | null => {
  const id = cameFrom
  cameFrom = null
  return id
}

/** A section remembers which of its cards is expanded. */
export const rememberExpanded = (
  sectionId: string,
  expanded: Expanded | null
) => {
  expandedBySection.set(sectionId, expanded)
}

/** The card a section had expanded, only when returning from a deep dive. */
export const recallExpanded = (sectionId: string): Expanded | null =>
  cameFrom === null ? null : (expandedBySection.get(sectionId) ?? null)

/** The id the home page's expanded card (or empty tile) carries, to scroll to. */
export const homeCardAnchor = (cardId: number) => `home-card-${cardId}`

const GAP = 16

const headerHeight = () =>
  parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(
      '--ln-header-height'
    )
  ) || 0

type ScrollOptions = {
  /** 'start' puts the top under the app header; 'nearest' scrolls the least that shows it all. */
  align: 'start' | 'nearest'
  /** Instant by default: 'start' runs before first paint. */
  smooth?: boolean
}

/** Scrolls a card into view below the sticky app header. */
export const scrollCardIntoView = (
  element: HTMLElement,
  { align, smooth = false }: ScrollOptions
) => {
  const top = headerHeight() + GAP
  const rect = element.getBoundingClientRect()
  const needsScroll =
    align === 'start' ||
    rect.height > window.innerHeight - top - GAP ||
    rect.top < headerHeight()
  let delta = rect.top - top
  if (!needsScroll) {
    if (rect.bottom <= window.innerHeight - GAP) return
    delta = rect.bottom - (window.innerHeight - GAP)
  }
  if (Math.abs(delta) < 1) return
  window.scrollTo({
    top: Math.max(0, window.scrollY + delta),
    behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'instant',
  })
}
